import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, TextField, AppBar, Toolbar,
  InputAdornment, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, Chip, Tooltip
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import api from '../api/api';

const DispositivosConfianza = () => {
  const [dispositivos, setDispositivos] = useState([]);
  const [originalDispositivos, setOriginalDispositivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nunca';
    const dateNum = Number(timestamp);
    if (isNaN(dateNum)) return 'Fecha inválida';

    return new Date(dateNum).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const fetchDispositivos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/dispositivos-confianza/');
      setDispositivos(res.data);
      setOriginalDispositivos(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al cargar dispositivos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDispositivos(); }, []);

  // Filtrado mejorado (incluye alias)
  useEffect(() => {
    const filtered = originalDispositivos.filter(d => 
      d.nombre_dispositivo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.device_token?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setDispositivos(filtered);
  }, [searchTerm, originalDispositivos]);

  // Actualizar alias
  const handleUpdateAlias = async (id) => {
    if (!editValue.trim() || editValue === dispositivos.find(d => d.id === id)?.alias) {
      setEditingId(null);
      return;
    }

    try {
      await api.put(`/dispositivos-confianza/${id}`, { alias: editValue.trim() });
      setSnackbar({ open: true, message: 'Alias actualizado correctamente', severity: 'success' });
      fetchDispositivos();
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al actualizar alias', severity: 'error' });
    } finally {
      setEditingId(null);
    }
  };

  const startEditing = (dispositivo) => {
    setEditingId(dispositivo.id);
    setEditValue(dispositivo.alias || '');
  };

  const handleToggleActivo = async (id, estadoActual) => {
    try {
      await api.put(`/dispositivos-confianza/${id}`, { activo: !estadoActual });
      setSnackbar({ 
        open: true, 
        message: estadoActual ? 'Dispositivo desactivado' : 'Dispositivo activado con éxito', 
        severity: 'success' 
      });
      fetchDispositivos();
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al cambiar estado', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este dispositivo permanentemente?')) return;
    try {
      await api.delete(`/dispositivos-confianza/${id}`);
      setSnackbar({ open: true, message: 'Dispositivo eliminado', severity: 'success' });
      fetchDispositivos();
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al eliminar', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#0f172a', mb: 3, borderRadius: 1 }}>
        <Toolbar>
          <DevicesIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Gestión de Dispositivos de Confianza</Typography>
          <TextField
            placeholder="Buscar por alias, nombre o token..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1, width: 320 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
          />
          <IconButton color="inherit" onClick={fetchDispositivos} sx={{ ml: 2 }}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell><b>Alias / Nombre del Dispositivo</b></TableCell>
                <TableCell><b>Token / ID</b></TableCell>
                <TableCell><b>Estado</b></TableCell>
                <TableCell><b>Código Verif.</b></TableCell>
                <TableCell><b>Último Acceso</b></TableCell>
                <TableCell align="center"><b>Acciones</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dispositivos.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {editingId === row.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => handleUpdateAlias(row.id)}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateAlias(row.id)}
                        autoFocus
                        placeholder="Alias del dispositivo"
                      />
                    ) : (
                      <Box>
                        <Typography 
                          variant="body1" 
                          fontWeight="medium"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => startEditing(row)}
                        >
                          {row.alias || row.nombre_dispositivo}
                          <IconButton size="small" sx={{ ml: 1, opacity: 0.6 }} onClick={() => startEditing(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Typography>
                        {row.alias && (
                          <Typography variant="caption" color="text.secondary">
                            {row.nombre_dispositivo}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell>
                    <code style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {row.device_token?.substring(0, 15)}...
                    </code>
                  </TableCell>

                  <TableCell>
                    <Chip 
                      label={row.activo ? "Autorizado" : "Bloqueado"} 
                      color={row.activo ? "success" : "error"}
                      icon={row.activo ? <CheckCircleIcon /> : <BlockIcon />}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', letterSpacing: 1 }}>
                      {row.codigo_verificacion || '---'}
                    </Typography>
                  </TableCell>

                  <TableCell>{formatDate(row.ultimo_uso)}</TableCell>

                  <TableCell align="center">
                    <Tooltip title={row.activo ? "Revocar Acceso" : "Autorizar Dispositivo"}>
                      <IconButton 
                        onClick={() => handleToggleActivo(row.id, row.activo)} 
                        color={row.activo ? "warning" : "success"}
                      >
                        {row.activo ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton onClick={() => handleDelete(row.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {dispositivos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No hay dispositivos registrados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DispositivosConfianza;